import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { GraduationCap, Mic, Square, Send, Loader2, Shuffle, RotateCcw, Sparkles, Stethoscope } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

/* KI-Verkaufstrainer (v8 M18, 22.10.2026): Einwandbehandlung im Rollenspiel üben. Die KI spielt eine erfundene Person,
   der Mitarbeiter antwortet per Text oder Sprache, nach 3–6 Runden kommt die Auswertung nach fünf festen Kriterien.
   Schalter KI_VERKAUFSTRAINER (Coolify); aus = Menüpunkt weg, die Seite nennt nur den Grund.
   Check-up-Angebot (28.10.2026): fünf feste Fälle als Kacheln — Lage nach dem Check-up, Angebot mit echten Preisen (vom
   Server beim Start festgehalten) und ein typischer Einwand. */

const fehler = (e, std) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : std);
const euro = (n) => (n == null ? '–' : `${Number(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
const datum = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '');

function ScoreZahl({ wert, gross }) {
  const farbe = wert >= 75 ? 'text-emerald-600 dark:text-emerald-400' : wert >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400';
  return <span className={`font-extrabold tabular-nums ${farbe} ${gross ? 'text-5xl' : 'text-lg'}`}>{wert ?? '–'}</span>;
}

export default function VerkaufstrainerPage() {
  const { user, isAdminPermissions } = useAuth();
  const leitung = user?.role === 'admin' || user?.role === 'manager' || isAdminPermissions;
  const [st, setSt] = useState(null);
  const [verlauf, setVerlauf] = useState(null);
  const [team, setTeam] = useState(null);
  const [params] = useSearchParams();      // „Übung des Tages“ auf /heute verlinkt mit ?zielgruppe=&einwand=
  const [wahl, setWahl] = useState({ zielgruppe: params.get('zielgruppe') || '', einwand: params.get('einwand') || '', fall: '' });
  const [sitzung, setSitzung] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState('');
  const [aufnahme, setAufnahme] = useState(null);
  const teile = useRef([]);
  const unten = useRef(null);

  const laden = useCallback(async () => {
    try {
      const [s, v] = await Promise.all([api.get('/verkaufstrainer/status'), api.get('/verkaufstrainer/verlauf')]);
      setSt(s.data); setVerlauf(v.data);
      if (leitung) api.get('/verkaufstrainer/team').then(r => setTeam(r.data.team)).catch(() => setTeam(null));
    } catch (e) { setSt({ verfuegbar: false, grund: fehler(e, 'Verkaufstrainer nicht erreichbar') }); }
  }, [leitung]);
  useEffect(() => { laden(); }, [laden]);
  useEffect(() => { unten.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [sitzung?.runden?.length, sitzung?.status]);

  const einwaende = (st?.einwaende || []).filter(e => !e.nur_fuer?.length || !wahl.zielgruppe || e.nur_fuer.includes(wahl.zielgruppe));
  const zufall = () => {
    const z = st.zielgruppen[Math.floor(Math.random() * st.zielgruppen.length)].key;
    const moegl = st.einwaende.filter(e => !e.nur_fuer?.length || e.nur_fuer.includes(z));
    setWahl({ zielgruppe: z, einwand: moegl[Math.floor(Math.random() * moegl.length)].key, fall: '' });
  };
  const bereit = !!wahl.fall || (!!wahl.zielgruppe && !!wahl.einwand);
  const starten = async () => {
    setBusy('start');
    const body = wahl.fall ? { fall: wahl.fall } : { zielgruppe: wahl.zielgruppe, einwand: wahl.einwand };
    try { setSitzung((await api.post('/verkaufstrainer/sitzungen', body)).data); setText(''); }
    catch (e) { toast.error(fehler(e, 'Übung nicht gestartet')); }
    setBusy('');
  };
  const senden = async () => {
    if (text.trim().length < 2) return;
    setBusy('senden');
    try { setSitzung((await api.post(`/verkaufstrainer/sitzungen/${sitzung.id}/antwort`, { text })).data); setText(''); }
    catch (e) { toast.error(fehler(e, 'Antwort nicht angekommen — bitte noch einmal senden')); }
    setBusy('');
  };
  const auswerten = async () => {
    setBusy('auswerten');
    try { setSitzung((await api.post(`/verkaufstrainer/sitzungen/${sitzung.id}/auswerten`)).data); laden(); }
    catch (e) { toast.error(fehler(e, 'Auswertung fehlgeschlagen')); }
    setBusy('');
  };
  const aufnehmen = async () => {
    if (aufnahme) { aufnahme.stop(); return; }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') { toast.error('Aufnahme in diesem Browser nicht möglich. Bitte tippen.'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const typ = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find(t => MediaRecorder.isTypeSupported?.(t)) || '';
      const rec = new MediaRecorder(stream, typ ? { mimeType: typ } : undefined);
      teile.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) teile.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setAufnahme(null);
        const b = new Blob(teile.current, { type: (rec.mimeType || 'audio/webm').split(';')[0] });
        const fd = new FormData(); fd.append('audio', b, b.type.includes('mp4') ? 'antwort.m4a' : 'antwort.webm');
        setBusy('sprache');
        try { const r = await api.post('/verkaufstrainer/sprache', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); setText(t => (t ? `${t} ` : '') + r.data.text); }
        catch (e) { toast.error(fehler(e, 'Nicht verstanden — bitte tippen')); }
        setBusy('');
      };
      rec.start(); setAufnahme(rec);
    } catch { toast.error('Mikrofon nicht freigegeben'); }
  };

  if (!st) return <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const kriterien = st.kriterien || [];
  const label = (key) => kriterien.find(k => k.key === key)?.label || key;
  const ausgewertet = sitzung?.status === 'ausgewertet';
  const laeuft = sitzung && !ausgewertet;

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-5" data-testid="verkaufstrainer-page">
      <div>
        <h1 className="text-2xl font-extrabold flex items-center gap-2"><GraduationCap className="w-6 h-6 text-marke" /> Verkaufstrainer</h1>
        <p className="text-sm text-muted-foreground">Einwände üben, bevor es im echten Gespräch zählt. Die KI spielt den Interessenten, du antwortest.</p>
      </div>

      {!st.verfuegbar && <Card><CardContent className="p-4 text-sm">{st.grund}</CardContent></Card>}

      {st.verfuegbar && verlauf && !sitzung && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[['Übungen', verlauf.anzahl], ['Ø letzte 10', verlauf.durchschnitt != null ? <ScoreZahl wert={verlauf.durchschnitt} /> : '–'],
            ['Letzte', verlauf.letzter != null ? <ScoreZahl wert={verlauf.letzter} /> : '–'], ['Üben', verlauf.schwaechstes ? label(verlauf.schwaechstes) : '–']]
            .map(([l, w]) => (
              <Card key={l}><CardContent className="p-3"><div className="text-xs text-muted-foreground">{l}</div><div className="text-lg font-bold leading-tight">{w}</div></CardContent></Card>
            ))}
        </div>
      )}

      {st.verfuegbar && !sitzung && (
        <Card><CardContent className="p-4 space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-semibold">Wer sitzt dir gegenüber?</p>
            <div className="flex flex-wrap gap-2">
              {st.zielgruppen.map(z => (
                <button key={z.key} onClick={() => setWahl(w => ({ fall: '', zielgruppe: z.key, einwand: (st.einwaende.find(e => e.key === w.einwand)?.nur_fuer || []).length && !st.einwaende.find(e => e.key === w.einwand).nur_fuer.includes(z.key) ? '' : w.einwand }))}
                  className={`rounded-full border px-3 py-1.5 text-sm ${wahl.zielgruppe === z.key ? 'border-marke bg-marke/10 text-marke font-semibold' : 'border-border'}`}>{z.label}</button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-semibold">Welcher Einwand kommt?</p>
            <div className="flex flex-wrap gap-2">
              {einwaende.map(e => (
                <button key={e.key} onClick={() => setWahl(w => ({ ...w, fall: '', einwand: e.key }))}
                  className={`rounded-full border px-3 py-1.5 text-sm text-left ${wahl.einwand === e.key ? 'border-marke bg-marke/10 text-marke font-semibold' : 'border-border'}`}>{e.label}</button>
              ))}
            </div>
          </div>
          {st.checkup_faelle?.length > 0 && (
            <div className="space-y-2" data-testid="vt-checkup-faelle">
              <p className="text-sm font-semibold flex items-center gap-1.5"><Stethoscope className="w-4 h-4 text-marke" /> Oder: Check-up-Angebot</p>
              <p className="text-xs text-muted-foreground">Ein Mitglied nach dem Check-up — du kennst das Ergebnis und verkaufst das passende Angebot.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {st.checkup_faelle.map(f => (
                  <button key={f.key} onClick={() => setWahl({ zielgruppe: '', einwand: '', fall: f.key })} data-testid={`vt-fall-${f.key}`}
                    className={`rounded-lg border p-3 text-left space-y-1 ${wahl.fall === f.key ? 'border-marke bg-marke/10' : 'border-border hover:border-marke/50'}`}>
                    <div className={`text-sm font-semibold ${wahl.fall === f.key ? 'text-marke' : ''}`}>{f.titel}</div>
                    <div className="text-xs text-muted-foreground">Ziel: {f.ziel}</div>
                    <div className="text-xs"><span className="text-muted-foreground">Angebot: </span>{f.angebot}</div>
                    <div className="text-xs italic text-muted-foreground">{f.einwand}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button className="bg-marke hover:bg-marke-dunkel gap-1.5" disabled={!bereit || !!busy} onClick={starten} data-testid="vt-starten">
              {busy === 'start' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Übung starten
            </Button>
            <Button variant="outline" className="gap-1.5" disabled={!!busy} onClick={zufall}><Shuffle className="w-4 h-4" /> Zufall</Button>
          </div>
          <p className="text-xs text-muted-foreground">Die Person ist erfunden, Preise kommen aus den aktiven Tarifen, beim Check-up aus den Coaching-Paketen und dem Ernährungscoaching. Ab {st.min_runden} Antworten kannst du auswerten, nach {st.max_runden} ist Schluss.</p>
        </CardContent></Card>
      )}

      {sitzung && (
        <Card><CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border/60 text-sm flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-semibold">{sitzung.person}</span>
            <span className="text-muted-foreground">{sitzung.szenario_label?.fall || sitzung.szenario_label?.zielgruppe} · {sitzung.szenario_label?.einwand}</span>
            <span className="ml-auto text-xs text-muted-foreground tabular-nums">Antwort {sitzung.eigene_antworten} von {st.max_runden}</span>
          </div>
          {sitzung.fall_info && (
            <div className="px-4 py-3 border-b border-border/60 text-sm space-y-2 bg-muted/30" data-testid="vt-fall-info">
              <p><span className="text-xs text-muted-foreground">Check-up: </span>{({ auf_kurs: 'auf Kurs', stabil: 'stabil', handlungsbedarf: 'Handlungsbedarf' })[sitzung.fall_info.lage?.bewertung] || sitzung.fall_info.lage?.bewertung}
                {sitzung.fall_info.lage?.huerden?.length > 0 && <> · Hürden: {sitzung.fall_info.lage.huerden.join(', ')}</>}
                {sitzung.fall_info.lage?.kein_plan && <> · kein Plan</>} · Ziel: {sitzung.fall_info.lage?.ziel}</p>
              <ul className="space-y-1">
                {sitzung.fall_info.angebot.map(a => (
                  <li key={a.schluessel} className="flex flex-wrap gap-x-2">
                    <span className="font-semibold">{a.name}</span>
                    <span className="tabular-nums">{euro(a.preis_eur)} {a.abrechnung}</span>
                    <span className="text-xs text-muted-foreground w-full">{[a.inhalt, a.gratis, a.anrechnung, a.raten].filter(Boolean).join(' · ')}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="p-4 space-y-3 max-h-[55vh] overflow-y-auto">
            {sitzung.runden.map((r, i) => (
              <div key={i} className={`flex ${r.von === 'mitarbeiter' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${r.von === 'mitarbeiter' ? 'bg-marke text-white rounded-br-sm' : 'bg-muted rounded-bl-sm'}`}>{r.text}</div>
              </div>
            ))}
            {busy === 'senden' && <div className="flex"><div className="rounded-2xl bg-muted px-3 py-2"><Loader2 className="w-4 h-4 animate-spin" /></div></div>}
            {sitzung.status === 'beendet' && <p className="text-xs text-center text-muted-foreground">Gespräch zu Ende — jetzt auswerten.</p>}
            <div ref={unten} />
          </div>
          {laeuft && (
            <div className="border-t border-border/60 p-3 space-y-2">
              {sitzung.status === 'laeuft' && (
                <div className="flex gap-2 items-end">
                  <textarea value={text} onChange={e => setText(e.target.value)} rows={2} maxLength={1000} placeholder="Deine Antwort …"
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); senden(); } }}
                    className="flex-1 min-w-0 rounded-md border border-input bg-background px-3 py-2 text-sm resize-none" data-testid="vt-text" />
                  <Button variant="outline" size="icon" title={aufnahme ? 'Aufnahme beenden' : 'Sprechen'} disabled={!!busy} onClick={aufnehmen}
                    className={aufnahme ? 'border-red-500 text-red-600' : ''}>{busy === 'sprache' ? <Loader2 className="w-4 h-4 animate-spin" /> : aufnahme ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}</Button>
                  <Button size="icon" className="bg-marke hover:bg-marke-dunkel" disabled={!!busy || text.trim().length < 2} onClick={senden} data-testid="vt-senden"><Send className="w-4 h-4" /></Button>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant={sitzung.auswertbar ? 'default' : 'outline'} disabled={!sitzung.auswertbar || !!busy} onClick={auswerten} data-testid="vt-auswerten">
                  {busy === 'auswerten' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Auswerten
                </Button>
                {!sitzung.auswertbar && <span className="text-xs text-muted-foreground">ab {st.min_runden} Antworten</span>}
                <button className="ml-auto text-xs underline text-muted-foreground" disabled={!!busy} onClick={() => { setSitzung(null); laden(); }}>abbrechen</button>
              </div>
            </div>
          )}
        </CardContent></Card>
      )}

      {ausgewertet && (
        <Card><CardContent className="p-4 space-y-4" data-testid="vt-ergebnis">
          <div className="flex items-center gap-4">
            <ScoreZahl wert={sitzung.gesamt} gross />
            <div className="text-sm"><p className="font-semibold">von 100 Punkten</p><p className="text-muted-foreground">{sitzung.zusammenfassung}</p></div>
          </div>
          <div className="space-y-3">
            {kriterien.map(k => {
              const s = sitzung.scores?.[k.key] || {};
              return (
                <div key={k.key} className={`rounded-lg border p-3 space-y-1.5 ${sitzung.schwaechstes === k.key ? 'border-amber-400' : 'border-border/60'}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold">{k.label}</span>
                    <span className="ml-auto tabular-nums text-xs text-muted-foreground">{s.punkte ?? 0} / 20</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-marke" style={{ width: `${((s.punkte || 0) / 20) * 100}%` }} /></div>
                  <p className="text-sm">{s.feedback}</p>
                  {s.bessere_formulierung && <p className="text-sm rounded-md bg-muted/60 px-2 py-1.5"><span className="text-xs text-muted-foreground">Besser: </span>„{s.bessere_formulierung}“</p>}
                </div>
              );
            })}
          </div>
          {sitzung.quelle_formulierung === 'ki_vorschlag' && <p className="text-xs text-muted-foreground">Die Formulierungen sind Vorschläge der KI. Sobald die FBC-Einwandskripte in der Wissensbasis stehen, richtet sich der Trainer danach.</p>}
          <Button className="gap-1.5" onClick={() => { setSitzung(null); setWahl({ zielgruppe: '', einwand: '', fall: '' }); laden(); }}><RotateCcw className="w-4 h-4" /> Neue Übung</Button>
        </CardContent></Card>
      )}

      {st.verfuegbar && !sitzung && verlauf?.sitzungen?.length > 0 && (
        <Card><CardContent className="p-4 space-y-2">
          <p className="text-sm font-semibold">Deine letzten Übungen</p>
          <ul className="divide-y divide-border/50 text-sm">
            {verlauf.sitzungen.slice(0, 10).map(s => (
              <li key={s.id} className="py-2 flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-12 tabular-nums">{datum(s.gestartet_am)}</span>
                <span className="flex-1 min-w-0 truncate">{s.szenario_label?.fall ? `Check-up: ${s.szenario_label.fall}` : s.szenario_label?.zielgruppe} · {s.szenario_label?.einwand}</span>
                {s.status === 'ausgewertet' ? <button title="Feedback ansehen" onClick={() => setSitzung(s)}><ScoreZahl wert={s.gesamt} /></button> : <button className="text-xs underline" onClick={() => setSitzung(s)}>weiter</button>}
              </li>
            ))}
          </ul>
        </CardContent></Card>
      )}

      {leitung && team?.length > 0 && !sitzung && (
        <Card><CardContent className="p-4 space-y-2">
          <p className="text-sm font-semibold">Team</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground"><tr><th className="text-left py-1 font-semibold">Name</th><th className="text-right py-1 font-semibold">Übungen</th><th className="text-right py-1 font-semibold">Ø</th><th className="text-left py-1 pl-3 font-semibold">üben</th></tr></thead>
              <tbody>{team.map(t => (
                <tr key={t.mitarbeiter_id} className="border-t border-border/40"><td className="py-1.5">{t.name}</td><td className="py-1.5 text-right tabular-nums">{t.anzahl}</td>
                  <td className="py-1.5 text-right"><ScoreZahl wert={t.durchschnitt} /></td><td className="py-1.5 pl-3 text-muted-foreground">{t.schwaechstes ? label(t.schwaechstes) : '–'}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}
